<!--
  The viewer page, cloning the layout of vendor-ref/mara/app/ui/src/views/nanome/WorkspaceDetails.vue:124-250
  (minus spotlight, network health, licence banner) with MARA's NTopbar reduced to a 4rem bar carrying the Logo,
  "Lite (Beta)" and the primary 'Open in Nanome' button. Left: ScenesPanel (one scene; New Scene opens a teaser).
  Below the canvas: the Sequences menu (SequenceSplit, pill at the bottom right while collapsed). Overlays on the canvas:
    bottom-left pill: Settings (skybox theme + night/day)            bottom-centre: Capture image / tool mode pill
    right-centre: tool bar (Default / Selection)                     empty state: dropzone + RCSB id + URL
  Right: WorkspacePanelRight (tabs; 32rem, collapsible to a 3rem rail; a bottom Drawer on mobile). Drag-and-drop anywhere loads files.
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onKeyStroke, useFullscreen } from '@vueuse/core';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import Toast from 'primevue/toast';
import { bindEngine, useLiteStore, type EngineBinding } from '@nanome-lite/state';
import { useDevice } from '@/composables/device';
import { installHostCallbacks, makeEngine } from '@/engine';
import { RETURN_KEY } from '@/open-in-nanome/oauth';
import { HANDOFF_KEY, loadHandoff } from '@/session/handoff';
import Logo from '@/components/Logo.vue';
import WorkspacePanelRight from '@/components/WorkspacePanelRight.vue';
import ViewerDropzone from '@/components/ViewerDropzone.vue';
import GlobalDropzone from '@/components/GlobalDropzone.vue';
import SettingsPopup from '@/components/SettingsPopup.vue';
import SnapshotPopup from '@/components/SnapshotPopup.vue';
import ToolBar from '@/components/ToolBar.vue';
import CanvasActions from '@/components/CanvasActions.vue';
import OpenInNanomeDialog from '@/open-in-nanome/OpenInNanomeDialog.vue';
import BetaNoticeDialog from '@/components/BetaNoticeDialog.vue';
import { ensureAccepted } from '@/composables/betaNotice';
import NanomeIcon from '@/components/NanomeIcon.vue';
import ScenesPanel from '@/components/ScenesPanel.vue';
import ScenesTeaser from '@/components/ScenesTeaser.vue';
import SequenceSplit from '@/components/SequenceSplit.vue';

const props = defineProps<{ openInNanomeOnMount?: boolean; handoffToken?: string | undefined }>();

const store = useLiteStore();
// The first load in this browser asks the user to acknowledge the beta notice (BetaNoticeDialog).
store.setLoadGuard(ensureAccepted);
const device = useDevice();
const toast = useToast();

const isEmbed = new URLSearchParams(location.search).has('embed');
const canvasHost = ref<HTMLElement | null>(null);
const settingsPopup = ref<InstanceType<typeof SettingsPopup>>();
const snapshotPopup = ref<InstanceType<typeof SnapshotPopup>>();
const scenesTeaser = ref<InstanceType<typeof ScenesTeaser>>();
const openInNanomeVisible = ref(false);

const isMobile = computed(() => device.isMobile.value);
const panelCollapsed = device.panelCollapsed;
// mobile bottom sheets start closed regardless of persisted desktop state (WorkspaceDetails.vue:32-36)
if (isMobile.value || isEmbed) panelCollapsed.value = true;
const rightSheetVisible = computed({ get: () => !panelCollapsed.value, set: v => { panelCollapsed.value = !v; } });

const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
watch(isFullscreen, v => { if (v) panelCollapsed.value = true; });

let binding: EngineBinding | null = null;
let uninstallHost: (() => void) | null = null;
onMounted(() => {
  // createEngine -> mount -> host callbacks (ribbon, surfaces) -> bindEngine (filters, then entries/components)
  const engine = makeEngine();
  if (canvasHost.value) engine.mount(canvasHost.value);
  uninstallHost = installHostCallbacks(engine, store);
  binding = bindEngine(store, engine);
  // RETURN_KEY: back from a full-page OAuth redirect; 'prompt' means the sign-in came from the prompt gate (the bar
  // restores its draft itself, so the Open in Nanome dialog stays closed). A fresh /open?s= link always opens it.
  const returnTo = sessionStorage.getItem(RETURN_KEY);
  const returning = props.openInNanomeOnMount || !!returnTo;
  const showDialog = !!props.handoffToken || returnTo !== 'prompt';
  // /open?s=<token> (ChatGPT handoff), or the same token again after a full-page OAuth redirect (either flow)
  const handoff = props.handoffToken ?? (returning ? sessionStorage.getItem(HANDOFF_KEY) ?? undefined : undefined);
  if (handoff) {
    sessionStorage.setItem(HANDOFF_KEY, handoff);
    sessionStorage.removeItem(RETURN_KEY);
    loadHandoff(store, handoff)
      .then(() => { if (showDialog) openInNanomeVisible.value = true; })
      .catch((e: unknown) => toast.add({ severity: 'error', summary: 'Open in Nanome', detail: e instanceof Error ? e.message : String(e), life: 10000 }));
  }
  else if (returning) {
    sessionStorage.removeItem(RETURN_KEY);
    if (showDialog) openInNanomeVisible.value = true;
  }
  // ?src=rcsb:4HHB or ?src=url:https://… (used by the extension iframe)
  const src = new URLSearchParams(location.search).get('src');
  if (src?.startsWith('rcsb:')) store.addEntryFromRcsb(src.slice(5)).catch(() => {});
  else if (src?.startsWith('url:')) store.addEntryFromUrl(src.slice(4)).catch(() => {});
});
onBeforeUnmount(() => { binding?.unbind(); uninstallHost?.(); binding?.engine.dispose(); });

// store toasts -> PrimeVue toasts
watch(() => store.toasts.length, () => {
  for (const t of store.toasts.splice(0)) {
    const detail = t.detail;
    toast.add(detail === undefined ? { severity: t.severity, summary: t.summary, life: 5000 } : { severity: t.severity, summary: t.summary, detail, life: 5000 });
  }
});

// Escape toggles the panel unless a menu/popover/dialog is open (WorkspaceDetails.vue:71-91); Cmd/Ctrl+Z undo/redo
onKeyStroke('Escape', () => {
  if (document.body.querySelector(':scope > .p-menu, :scope > .p-popover, :scope > .p-dialog-mask, :scope > .p-drawer-mask')) return;
  const el = document.activeElement as HTMLElement | null;
  if (el && (['BUTTON', 'INPUT', 'TEXTAREA'].includes(el.tagName) || el.isContentEditable)) { el.blur(); return; }
  panelCollapsed.value = !panelCollapsed.value;
});
onKeyStroke((e) => {
  const mod = device.isMac ? e.metaKey : e.ctrlKey;
  if (!mod || e.key.toLowerCase() !== 'z') return;
  const el = document.activeElement as HTMLElement | null;
  if (el && (['INPUT', 'TEXTAREA'].includes(el.tagName) || el.isContentEditable)) return;
  e.preventDefault();
  if (e.shiftKey) store.redo(); else store.undo();
});
</script>

<template>
  <div class="h-full w-full flex flex-col">
    <Toast position="bottom-right" />
    <GlobalDropzone />

    <!-- top bar (NTopbar.vue reduced): 4rem, logo left, Open in Nanome right -->
    <header v-if="!isEmbed" class="shrink-0 h-[var(--topbar-height)] px-4 flex items-center gap-2 bg-ground" aria-label="Nanome Lite (Beta)">
      <Logo responsive />
      <span class="text-lg font-600 leading-none ws-nowrap" data-testid="brand-lite">Lite <span class="text-sm font-400 op-60">(Beta)</span></span>
      <div class="ml-auto flex items-center gap-2">
        <Button
          v-if="isMobile"
          v-tooltip.bottom="'Entries'"
          icon="i-tabler-layout-navbar"
          severity="secondary"
          rounded
          text
          @click="panelCollapsed = !panelCollapsed"
        />
        <Button
          :label="isMobile ? undefined : 'Open in Nanome'"
          :aria-label="isMobile ? 'Open in Nanome' : undefined"
          rounded
          data-testid="topbar-open-in-nanome"
          @click="openInNanomeVisible = true"
        >
          <template #icon="iconProps">
            <NanomeIcon :class="iconProps.class" />
          </template>
        </Button>
      </div>
    </header>

    <div class="min-h-0 flex-1 flex">
      <!-- left: scenes panel (WorkspaceScenesPanel.vue); Lite has one scene -->
      <div v-if="!isEmbed && !isMobile" class="z-100">
        <ScenesPanel @teaser="scenesTeaser?.show($event)" />
      </div>

      <!-- canvas + overlays, with the Sequences menu split below it -->
      <SequenceSplit class="flex-1" :enabled="!isEmbed" :pill-class="'bottom-2'">
      <div class="relative min-w-0 flex-1 select-none of-hidden">
        <div ref="canvasHost" class="absolute inset-0" />
        <ViewerDropzone />

        <div class="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
          <CanvasActions @capture="snapshotPopup?.show($event)" @scenes-teaser="scenesTeaser?.show($event)" />
        </div>
        <SnapshotPopup ref="snapshotPopup" />

        <div class="absolute top-1/2 right-2 -translate-y-1/2 z-10">
          <ToolBar />
        </div>

        <div class="absolute bottom-2 left-2 z-10 flex items-center bg-xr rd-full p-1">
          <Button v-tooltip.top="'Settings'" icon="i-tabler-settings" size="small" severity="secondary" rounded text @click="settingsPopup?.show($event)" />
          <Button
            v-if="isMobile"
            v-tooltip.top="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
            :icon="isFullscreen ? 'i-tabler-arrows-diagonal-minimize-2' : 'i-tabler-arrows-diagonal'"
            size="small"
            severity="secondary"
            rounded
            text
            @click="toggleFullscreen()"
          />
          <SettingsPopup ref="settingsPopup" />
        </div>

        <div v-if="store.isBusy && store.entries.length" class="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-xr rd-full px-4 py-2 text-sm">
          <i class="i-tabler-loader-2 animate-spin" /> Loading {{ store.loadingNames.join(', ') }}
        </div>
      </div>
      </SequenceSplit>

      <!-- right panel: 32rem / 3rem rail on desktop, bottom drawer on mobile -->
      <div v-if="!isEmbed && !isMobile" class="z-100">
        <WorkspacePanelRight v-model:collapsed="panelCollapsed" @open-in-nanome="openInNanomeVisible = true" />
      </div>
      <Drawer
        v-if="!isEmbed && isMobile"
        v-model:visible="rightSheetVisible"
        class="h-85dvh! rd-t-3 of-hidden"
        position="bottom"
        :show-close-icon="false"
        pt:header="hidden"
        pt:content="p-0! flex flex-col"
      >
        <WorkspacePanelRight v-model:collapsed="panelCollapsed" @open-in-nanome="openInNanomeVisible = true" />
      </Drawer>
    </div>

    <ScenesTeaser ref="scenesTeaser" @open-in-nanome="openInNanomeVisible = true" />
    <OpenInNanomeDialog v-model:visible="openInNanomeVisible" />
    <BetaNoticeDialog />
  </div>
</template>
